import React, { useMemo, useState } from "react";
import { Eye, EyeOff, Check } from "lucide-react";

function score(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

export default function PasswordField({ label = "Password", value, onChange, error, id = "password", showStrength = true }) {
  const [show, setShow] = useState(false);
  const s = useMemo(() => score(value || ""), [value]);
  const okLen = (value || "").length >= 8;
  const okMix = /[A-Za-z]/.test(value || "") && /\d/.test(value || "");
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#2A3040", "#EF4444", "#F59E0B", "#84CC16", "#22C55E"];

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-[#C4C9D4]">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          className={`w-full rounded-lg border bg-[#151A24] px-3.5 py-3 pr-11 text-[15px] text-[#F4F5F8] placeholder-[#6E7686] outline-none transition-colors focus:border-[#6D5EF6] focus:ring-1 focus:ring-[#6D5EF6] ${
            error ? "border-red-400" : "border-[#2A3040]"
          }`}
          data-testid="password-input"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7686] hover:text-[#AAB1BF]"
          aria-label={show ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
        </button>
      </div>
      {error && <p className="mt-1.5 text-[12px] text-red-500">{error}</p>}
      {showStrength && (value || "").length > 0 && (
        <div className="mt-2.5">
          <div className="flex gap-1.5">
            {[1, 2, 3, 4].map((n) => (
              <span
                key={n}
                className="h-1 flex-1 rounded-full transition-colors"
                style={{ background: n <= s ? colors[s] : "#242A38" }}
              />
            ))}
          </div>
          <div className="mt-2 flex items-center gap-4 text-[11px] text-[#98A0B0]">
            <span className={`inline-flex items-center gap-1 ${okLen ? "text-[#16A34A]" : ""}`}>
              <Check className={`h-3 w-3 ${okLen ? "opacity-100" : "opacity-30"}`} /> 8+ characters
            </span>
            <span className={`inline-flex items-center gap-1 ${okMix ? "text-[#16A34A]" : ""}`}>
              <Check className={`h-3 w-3 ${okMix ? "opacity-100" : "opacity-30"}`} /> letter & number
            </span>
            {s >= 1 && <span className="ml-auto" style={{ color: colors[s] }}>{labels[s]}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
